/**
 * Add Set Modal Component
 *
 * Form for manually logging a set
 * Touch-friendly inputs with large targets
 * Intensity displayed as 1-5 stars
 * For cardio/other workouts, weight and reps are optional
 * Supports pre-populated exercise name when adding to existing exercise
 */

import { useState, type FormEvent } from 'react';
import type { WorkoutType } from '@/hooks/useWorkout';
import './AddSetModal.css';

interface AddSetData {
  exercise: string;
  reps?: number;
  weight?: number;
  intensity?: number;
}

interface AddSetModalProps {
  workoutType?: WorkoutType | null;
  initialExercise?: string | undefined;
  onClose: () => void;
  onSubmit: (data: AddSetData) => void;
}

export function AddSetModal({ workoutType, initialExercise, onClose, onSubmit }: AddSetModalProps): JSX.Element {
  const [exercise, setExercise] = useState(initialExercise ?? '');
  const [reps, setReps] = useState('');
  const [weight, setWeight] = useState('');
  const [intensity, setIntensity] = useState<number>(0);
  const [exerciseError, setExerciseError] = useState<string | null>(null);

  // For cardio and other workouts, weight and reps are optional
  const isCardioOrOther = workoutType === 'Cardio' || workoutType === 'Other';

  // If exercise is pre-populated, user shouldn't need to edit it
  const isExerciseLocked = Boolean(initialExercise);

  const validateExerciseName = (name: string): boolean => {
    const trimmed = name.trim();
    if (!trimmed) {
      setExerciseError('Exercise name is required');
      return false;
    }
    if (trimmed.length < 2) {
      setExerciseError('Exercise name must be at least 2 characters');
      return false;
    }
    setExerciseError(null);
    return true;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const trimmedExercise = exercise.trim();

    if (!validateExerciseName(trimmedExercise)) {
      return;
    }

    // For strength workouts (Push, Pull, Leg), require reps
    if (!isCardioOrOther && !reps) {
      return;
    }

    const data: AddSetData = {
      exercise: trimmedExercise,
    };

    if (reps) {
      data.reps = parseInt(reps, 10);
    }

    if (weight) {
      data.weight = parseFloat(weight);
    }

    if (intensity > 0) {
      data.intensity = intensity;
    }

    onSubmit(data);
  };

  const handleExerciseChange = (value: string): void => {
    setExercise(value);
    if (exerciseError) {
      validateExerciseName(value);
    }
  };

  const handleStarClick = (star: number): void => {
    setIntensity(star === intensity ? 0 : star);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">
            {isExerciseLocked ? `Add Set - ${initialExercise}` : 'Add Set'}
          </h2>
          <button type="button" className="modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          {!isExerciseLocked && (
            <div className="modal__field">
              <label htmlFor="exercise" className="modal__label">
                Exercise Name *
              </label>
              <input
                id="exercise"
                type="text"
                className={`modal__input ${exerciseError ? 'modal__input--error' : ''}`}
                value={exercise}
                onChange={(e) => handleExerciseChange(e.target.value)}
                onBlur={() => validateExerciseName(exercise)}
                placeholder="Bench Press"
                required
                autoFocus
              />
              {exerciseError && (
                <span className="modal__error">{exerciseError}</span>
              )}
            </div>
          )}

          {!isCardioOrOther && (
            <div className="modal__row">
              <div className="modal__field modal__field--half">
                <label htmlFor="weight" className="modal__label">
                  Weight (lbs)
                </label>
                <input
                  id="weight"
                  type="number"
                  className="modal__input"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="225"
                  step="0.5"
                  min="0"
                  autoFocus={isExerciseLocked}
                />
              </div>

              <div className="modal__field modal__field--half">
                <label htmlFor="reps" className="modal__label">
                  Reps *
                </label>
                <input
                  id="reps"
                  type="number"
                  className="modal__input"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  placeholder="8"
                  min="1"
                  required
                />
              </div>
            </div>
          )}

          {isCardioOrOther && (
            <div className="modal__field">
              <label htmlFor="notes" className="modal__label">
                Notes (Optional)
              </label>
              <input
                id="notes"
                type="text"
                className="modal__input"
                placeholder="e.g., 20 min run, 5k distance"
                autoFocus={isExerciseLocked}
              />
            </div>
          )}

          <div className="modal__field">
            <label className="modal__label">
              Intensity (1-5)
            </label>
            <div className="modal__star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`modal__star ${intensity >= star ? 'modal__star--active' : ''}`}
                  onClick={() => handleStarClick(star)}
                >
                  ⭐
                </button>
              ))}
            </div>
          </div>

          <div className="modal__actions">
            <button
              type="button"
              className="modal__button modal__button--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal__button modal__button--primary"
            >
              Add Set
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
